/**
 * `/api/workflows/:slug/trace` — one workflow's trigger trace (RO-3-13).
 *
 * ## Why this is a REWRITE
 *
 * `excellent/app/agents/workflows/[slug]/trace/page.tsx` (156 LOC) calls THREE
 * separate server seams inline while rendering — `getLoopBySlug`,
 * `listLoopTrace` (`@excellent/core/server`) and `readTriggerCardState` — then
 * hands only the row array to a `"use client"` table. The stat strip and the
 * "rails in force" band are computed and rendered in the server body, so a
 * mechanical port would leave two thirds of the page behind.
 *
 * ## Decision — ONE endpoint, not three
 *
 * The three reads compose into a single response because the page cannot render
 * a correct partial: the summary strip and the rails band are only meaningful
 * together (a "held back: 4" count with no debounce/cap alongside it is the
 * "mystery rather than an explanation" the page's own comment warns about), and
 * a missing subscription switches the whole page to its empty state. Three
 * endpoints would let the browser paint a strip whose rails had not arrived —
 * re-admitting exactly the disagreement the original avoided by reading all
 * three in one pass.
 *
 * `detail: null` carries not-found rather than a 404, matching the sibling
 * `workflowDetail.get` convention that RO-3-02b established for this namespace,
 * so the route renders its own not-found with the breadcrumb intact (the
 * original's `notFound()`).
 *
 * ## Decision — the summary is the WHOLE ledger, and that is preserved
 *
 * `listLoopTrace` deliberately aggregates the full firing ledger for the
 * summary while returning only `limit` rows, because "a stat strip that
 * silently described only the most recent page would understate a busy
 * trigger". The contract keeps rows and summary as separate fields for exactly
 * that reason — a client that recomputed the summary from `rows` would
 * reintroduce the dishonesty. Nothing downstream should aggregate `rows`.
 */

import { z } from "zod";
import type { Operation } from "../../types";

/** One firing DECISION — not one run. A row with no run is the useful kind. */
export interface TriggerTraceRowDto {
  subscription_uid: string;
  subscription_name: string;
  event_uid: string;
  event_type: string;
  /** 'fired' | 'debounced' | 'rate_capped'. NULL (pre-v143) reads as 'fired'. */
  outcome: string;
  fired_at: string;
  consumed_at: string | null;
  dispatch_detail: string | null;
  run_uid: string | null;
  payload_json: string | null;
}

export interface TriggerTraceSummaryDto {
  fired: number;
  debounced: number;
  rate_capped: number;
  dispatched: number;
  failed: number;
  /** Fired but not yet consumed — queued for the next tick. */
  pending: number;
}

/** The trigger's configured rails — what makes a "held back" count explicable. */
export interface TraceSubscriptionDto {
  event_type_prefix: string;
  aggregate_kind: string | null;
  debounce_seconds: number;
  max_fires_per_hour: number;
  enabled: boolean;
}

export interface WorkflowTraceDetail {
  slug: string;
  title: string;
  rows: TriggerTraceRowDto[];
  summary: TriggerTraceSummaryDto;
  /** null = no trigger armed; the page switches to its empty state. */
  subscription: TraceSubscriptionDto | null;
  /** Whether the dispatcher will act on a fire at all. */
  autorunEnabled: boolean;
  /** Whether the scheduler process is actually alive. */
  drainerLive: boolean;
}

const traceQuery = z.object({
  limit: z.coerce.number().int().positive().max(500).optional(),
});
export type WorkflowTraceQuery = z.infer<typeof traceQuery>;

const getOp: Operation<
  { slug: string },
  WorkflowTraceQuery,
  void,
  { detail: WorkflowTraceDetail | null }
> = {
  method: "GET",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/trace`,
  params: z.object({ slug: z.string().min(1) }),
  query: traceQuery,
  response: z.custom<{ detail: WorkflowTraceDetail | null }>(),
};

export const workflowTrace = {
  get: getOp,
};
